import React, { useState, useEffect, useCallback } from 'react';
import {
  getClaudeTokens,
  createClaudeToken,
  updateClaudeToken,
  getClaudeTokenUsage,
  revokeClaudeToken,
  saveClaudeSettings,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { KernelPage } from '../kernelAdminShared';
import { fmtDate, fmtDateTime } from '../utils/kernelFormat';
import '../components/kernelClaude.css';

// Scadenze proposte (giorni da oggi); "0" = non scade mai.
const EXPIRY_CHOICES = ['30', '90', '365', '0'];
const EXPIRING_DAYS = 7;
const STATUS_BADGE = { active: 'badge-success', expired: 'badge-warning', revoked: 'badge-warning' };

/** Barre degli ultimi giorni di un token: richieste (e quante con errore). */
function UsageBars({ days, t }) {
  const max = Math.max(1, ...days.map((d) => d.requests));
  return (
    <div className="kc-usage" aria-label={t('claude.usageDaily')}>
      {days.map((d) => (
        <div key={d.day} className="kc-usage-col" title={t('claude.usageDay', { day: d.day.slice(8, 10) + '/' + d.day.slice(5, 7), requests: d.requests, errors: d.errors })}>
          <div className="kc-usage-bar" style={{ height: `${Math.max(d.requests > 0 ? 4 : 1, (d.requests / max) * 40)}px` }}>
            {d.errors > 0 && <div className="kc-usage-err" style={{ height: `${(d.errors / d.requests) * 100}%` }} />}
          </div>
          <span>{d.day.slice(8, 10)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Pagina "Claude" di ogni app: token personali per collegare Claude (server MCP dell'app) ai propri dati,
 * con gli stessi permessi dell'utente. Ognuno gestisce i suoi; chi gestisce gli utenti vede e può revocare
 * anche quelli dell'azienda (ad esempio per disattivare l'accesso di una persona).
 */
function ClaudeConnector({ user, onLogout }) {
  const { t } = useLanguage();
  const [tokens, setTokens] = useState([]);
  const [canManageAll, setCanManageAll] = useState(false);
  const [scope, setScope] = useState('mine');
  const [mcpUrl, setMcpUrl] = useState('');
  const [rateLimit, setRateLimit] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ name: '', days: '90' });
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [savingLimit, setSavingLimit] = useState(false);
  const [usageOpen, setUsageOpen] = useState({});

  const load = useCallback(async (nextScope) => {
    setLoading(true);
    try {
      const data = await getClaudeTokens(nextScope);
      setTokens(data.tokens || []);
      setCanManageAll(!!data.can_manage_all);
      setMcpUrl(data.mcp_url || '');
      setRateLimit(data.rate_limit_per_minute ? String(data.rate_limit_per_minute) : '');
    } catch (err) {
      setError(errorMessage(err, t('claude.loadError')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(scope);
  }, [scope, load]);

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    resetMessages();
    setCreating(true);
    try {
      const res = await createClaudeToken({ name: form.name.trim(), expires_in_days: Number(form.days) || null });
      setRevealed({ name: form.name.trim(), url: res.connector_url });
      setForm({ ...form, name: '' });
      await load(scope);
    } catch (err) {
      setError(errorMessage(err, t('claude.createError')));
    } finally {
      setCreating(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(revealed.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      setError(t('claude.copyError'));
    }
  };

  const handleRevoke = async (token) => {
    const message = token.is_mine
      ? t('claude.confirmRevoke', { name: token.name })
      : t('claude.confirmRevokeOther', { name: token.name, owner: `${token.owner_name} ${token.owner_surname}`.trim() });
    if (!window.confirm(message)) return;
    resetMessages();
    try {
      await revokeClaudeToken(token.id);
      setSuccess(t('claude.revoked'));
      await load(scope);
    } catch (err) {
      setError(errorMessage(err, t('claude.revokeError')));
    }
  };

  const handleExpiry = async (token, days) => {
    if (days === '') return;
    resetMessages();
    try {
      await updateClaudeToken(token.id, { expires_in_days: Number(days) || null });
      setSuccess(t('claude.expiryUpdated'));
      await load(scope);
    } catch (err) {
      setError(errorMessage(err, t('claude.expiryError')));
    }
  };

  const handleSaveLimit = async (e) => {
    e.preventDefault();
    resetMessages();
    setSavingLimit(true);
    try {
      await saveClaudeSettings({ rate_limit_per_minute: rateLimit.trim() === '' ? null : Number(rateLimit) });
      setSuccess(t('claude.settingsSaved'));
    } catch (err) {
      setError(errorMessage(err, t('claude.settingsError')));
    } finally {
      setSavingLimit(false);
    }
  };

  const toggleUsage = async (token) => {
    if (usageOpen[token.id]) {
      setUsageOpen({ ...usageOpen, [token.id]: null });
      return;
    }
    try {
      const res = await getClaudeTokenUsage(token.id, 14);
      setUsageOpen({ ...usageOpen, [token.id]: res.days });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  // Token attivi che scadono a breve: avviso in cima alla pagina.
  const expiring = tokens.filter((tk) => tk.status === 'active' && tk.expires_at && (new Date(tk.expires_at.replace(' ', 'T')).getTime() - Date.now()) < EXPIRING_DAYS * 86400000);
  const expiryText = (token) => (token.expires_at ? fmtDate(token.expires_at) : t('claude.never'));
  const ownerName = (token) => `${token.owner_name} ${token.owner_surname}`.trim();

  return (
    <KernelPage user={user} onLogout={onLogout}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('claude.title')}</h1>
          <p className="page-subtitle">{t('claude.subtitle')}</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <section className="card kc-card">
        <h3 className="kc-title">{t('claude.howTitle')}</h3>
        <ol className="kc-steps">
          <li>{t('claude.step1')}</li>
          <li>{t('claude.step2')}</li>
          <li>{t('claude.step3')}</li>
        </ol>
        <p className="kc-hint">{t('claude.howHint')}</p>
        {mcpUrl && <p className="kc-hint">{t('claude.baseUrl')} <code className="kc-code">{mcpUrl}/&lt;token&gt;</code></p>}
      </section>

      {expiring.length > 0 && (
        <div className="warning-message" role="alert">
          <strong>{t('claude.expiringTitle')}</strong>
          <ul className="kc-list">
            {expiring.map((tk) => (
              <li key={tk.id}>{t('claude.expiringItem', { name: tk.name, owner: ownerName(tk), date: fmtDateTime(tk.expires_at) })}</li>
            ))}
          </ul>
          <span>{t('claude.expiringHint')}</span>
        </div>
      )}

      {revealed && (
        <div className="card kc-secret">
          <h3 className="kc-title">{t('claude.revealTitle')} — {revealed.name}</h3>
          <p className="kc-hint">{t('claude.revealHint')}</p>
          <div className="kc-secret-code">
            <code>{revealed.url}</code>
            <button className="btn btn-secondary btn-sm" type="button" onClick={copyUrl}>
              {copied ? t('claude.copied') : t('claude.copy')}
            </button>
          </div>
          <button className="btn" type="button" onClick={() => setRevealed(null)}>{t('claude.revealClose')}</button>
        </div>
      )}

      <section className="card kc-card">
        <h3 className="kc-title">{t('claude.newTitle')}</h3>
        <form className="kc-new" onSubmit={handleCreate}>
          <div className="form-group kc-new-name">
            <label>{t('claude.tokenName')}</label>
            <input value={form.name} maxLength={100} placeholder={t('claude.tokenNamePlaceholder')} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="form-group kc-new-expiry">
            <label>{t('claude.expiry')}</label>
            <select value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })}>
              {EXPIRY_CHOICES.map((d) => (
                <option key={d} value={d}>{t(`claude.exp_${d}`)}</option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn" disabled={creating || form.name.trim() === ''}>
            {creating ? t('common.saving') : t('claude.create')}
          </button>
        </form>
        <p className="kc-hint">{t('claude.expiryHint')}</p>
      </section>

      <section className="card kc-card">
        <div className="kc-list-head">
          <h3 className="kc-title">{scope === 'company' ? t('claude.companyTokens') : t('claude.myTokens')}</h3>
          {canManageAll && (
            <div className="kc-scope" role="group" aria-label={t('claude.scope')}>
              <button type="button" className={'btn btn-sm ' + (scope === 'mine' ? '' : 'btn-secondary')} onClick={() => setScope('mine')}>{t('claude.scopeMine')}</button>
              <button type="button" className={'btn btn-sm ' + (scope === 'company' ? '' : 'btn-secondary')} onClick={() => setScope('company')}>{t('claude.scopeCompany')}</button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : tokens.length === 0 ? (
          <p className="kc-hint">{t('claude.noTokens')}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('claude.colName')}</th>
                  {scope === 'company' && <th>{t('claude.colOwner')}</th>}
                  <th>{t('claude.colStatus')}</th>
                  <th>{t('claude.colExpires')}</th>
                  <th>{t('claude.colLastUsed')}</th>
                  <th>{t('claude.colUsage')}</th>
                  <th>{t('claude.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <tr key={token.id} className={token.status === 'active' ? '' : 'row-inactive'}>
                    <td>
                      <div className="kc-token-name">{token.name}</div>
                      <div className="kc-muted">{token.token_prefix}… · {t('claude.createdOn', { date: fmtDate(token.created_at) })}</div>
                    </td>
                    {scope === 'company' && (
                      <td>
                        {ownerName(token)}
                        {token.is_mine && <span className="kc-muted"> ({t('claude.you')})</span>}
                      </td>
                    )}
                    <td><span className={`badge ${STATUS_BADGE[token.status] || 'badge-warning'}`}>{t(`claude.status_${token.status}`)}</span></td>
                    <td className="nowrap">{expiryText(token)}</td>
                    <td className="nowrap">{token.last_used_at ? fmtDateTime(token.last_used_at) : <span className="kc-muted">{t('claude.neverUsed')}</span>}</td>
                    <td>
                      <button type="button" className="kc-usage-btn" onClick={() => toggleUsage(token)} title={t('claude.usageShow')}>
                        <strong>{token.usage.today}</strong> <span className="kc-muted">{t('claude.usageToday')}</span>
                        <span className="kc-muted"> · {token.usage.last7} {t('claude.usage7')}</span>
                        {token.usage.errors7 > 0 && <span className="kc-usage-errors"> · {token.usage.errors7} {t('claude.usageErrors')}</span>}
                      </button>
                      {usageOpen[token.id] && <UsageBars days={usageOpen[token.id]} t={t} />}
                    </td>
                    <td>
                      {token.status !== 'revoked' && (
                        <div className="kc-actions">
                          <select value="" onChange={(e) => handleExpiry(token, e.target.value)} aria-label={t('claude.changeExpiry')}>
                            <option value="">{t('claude.changeExpiry')}</option>
                            {EXPIRY_CHOICES.map((d) => (
                              <option key={d} value={d}>{t(`claude.exp_${d}`)}</option>
                            ))}
                          </select>
                          <button className="btn btn-secondary btn-sm" type="button" onClick={() => handleRevoke(token)} title={t('claude.revokeTitle')}>
                            {t('claude.revoke')}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {canManageAll && (
        <section className="card kc-card">
          <h3 className="kc-title">{t('claude.settingsTitle')}</h3>
          <form className="kc-new" onSubmit={handleSaveLimit}>
            <div className="form-group kc-new-expiry">
              <label>{t('claude.rateLimit')}</label>
              <input type="number" min="1" max="100000" value={rateLimit} placeholder={t('claude.rateLimitPlaceholder')} onChange={(e) => setRateLimit(e.target.value)} />
            </div>
            <button type="submit" className="btn" disabled={savingLimit}>
              {savingLimit ? t('common.saving') : t('common.save')}
            </button>
          </form>
          <p className="kc-hint">{t('claude.rateLimitHint')}</p>
        </section>
      )}
    </KernelPage>
  );
}

export default ClaudeConnector;
